import { forwardRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import SportFundLogo from './SportFundLogo';

/**
 * Split-pane chrome used by /signin, /signup, /signup-creator, /password-reset.
 * Left (dark brand) panel hides on mobile; right side is the form on a clean
 * white panel. No top header — the logo lives in the brand panel and a small
 * "Back to home" link sits in the corner of the form panel.
 */
export default function AuthLayout({
  title,
  subtitle,
  children,
  bullets,
  variant = 'athlete',
  footer,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  bullets?: string[];
  variant?: 'athlete' | 'supporter' | 'reset';
  footer?: ReactNode;
}) {
  const defaultBullets =
    variant === 'reset'
      ? ['Free forever for athletes', 'No card needed', 'Two minutes to launch']
      : ['Free forever for athletes', '1–3 day payouts to your bank', 'Cancel any time, no questions'];
  const items = bullets ?? defaultBullets;

  return (
    <div className="min-h-screen grid lg:grid-cols-12 bg-white antialiased">
      {/* Brand panel */}
      <aside className="hidden lg:flex lg:col-span-5 xl:col-span-5 relative overflow-hidden bg-[#070b18] text-white p-12 flex-col justify-between">
        <div
          aria-hidden
          className="absolute inset-0 bg-[radial-gradient(120%_80%_at_30%_-10%,rgba(30,64,175,0.5),transparent_60%),radial-gradient(80%_60%_at_100%_100%,rgba(250,204,21,0.2),transparent_55%)]"
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:24px_24px]"
        />
        <div
          aria-hidden
          className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-blue-600/30 blur-3xl"
          style={{ animation: 'blobFloat1 18s ease-in-out infinite' }}
        />
        <div
          aria-hidden
          className="absolute -bottom-32 right-0 h-[460px] w-[460px] rounded-full bg-yellow-400/15 blur-3xl"
          style={{ animation: 'blobFloat2 22s ease-in-out infinite' }}
        />

        <div className="relative">
          <Link to="/" aria-label="Sport Fund — home">
            <SportFundLogo className="h-10 text-white" accent="#FACC15" />
          </Link>
        </div>

        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-yellow-300/90">
            <Sparkles className="h-3.5 w-3.5" />
            {variant === 'supporter' ? 'For supporters' : variant === 'reset' ? 'Reset access' : 'For athletes chasing something'}
          </div>
          <h2 className="mt-6 text-4xl xl:text-5xl font-extrabold tracking-tight leading-[0.98]">
            Train hard.{' '}
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-300 bg-clip-text text-transparent">
              Don&apos;t carry the season alone.
            </span>
          </h2>
          <p className="mt-5 text-base xl:text-lg text-slate-300 leading-relaxed max-w-md">
            Sport Fund is the free, athlete-first platform that lets family, friends, club and sponsors back your career — straight to your bank account.
          </p>
          <ul className="mt-7 space-y-3">
            {items.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-200">
                <Check className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative text-sm text-slate-400">
          <Link to="/fund/demo" className="inline-flex items-center gap-2 text-slate-200 hover:text-white transition-colors">
            <span className="rounded-full bg-yellow-400 text-slate-900 text-[10px] font-bold tracking-wider px-2 py-0.5 uppercase">Demo</span>
            See a real fund page →
          </Link>
        </div>
      </aside>

      {/* Form panel */}
      <section className="lg:col-span-7 xl:col-span-7 relative flex flex-col">
        <div className="px-6 pt-6 flex items-center justify-between">
          <Link to="/" className="lg:hidden" aria-label="Sport Fund — home">
            <SportFundLogo className="h-9 text-slate-900" accent="#1E40AF" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 transition-colors ml-auto">
            ← Back to home
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10 md:py-16">
          <div className="w-full max-w-md">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-3 text-slate-600 leading-relaxed">{subtitle}</p>
            )}
            <div className="mt-8">{children}</div>
            {footer && <div className="mt-8 text-sm text-slate-500">{footer}</div>}
          </div>
        </div>
      </section>
    </div>
  );
}

// Reusable input matching the rest of the design system.
// forwardRef is required so React Hook Form's `register()` can attach to the
// underlying <input>; without it the form never tracks the field's value.
export const AuthInput = forwardRef<
  HTMLInputElement,
  {
    label: string;
    error?: string;
    hint?: string;
  } & React.InputHTMLAttributes<HTMLInputElement>
>(function AuthInput({ label, error, hint, ...inputProps }, ref) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-800">{label}</span>
      <input
        ref={ref}
        {...inputProps}
        className="mt-1.5 block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
      />
      {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
      {error && <span className="mt-1 block text-sm text-red-600">{error}</span>}
    </label>
  );
});

/** Primary auth button — full width, dark navy. */
export function AuthSubmit({
  children,
  loading,
  ...buttonProps
}: { children: ReactNode; loading?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...buttonProps}
      disabled={loading || buttonProps.disabled}
      className="w-full inline-flex items-center justify-center rounded-full bg-blue-900 hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3.5 text-base font-semibold shadow-lg shadow-blue-900/20 transition-all active:scale-[0.99]"
    >
      {loading ? 'Just a sec…' : children}
    </button>
  );
}

/** Divider with text label, used to separate email + social auth. */
export function AuthDivider({ label }: { label: string }) {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-slate-200" />
      </div>
      <div className="relative flex justify-center text-xs">
        <span className="bg-white px-3 text-slate-400 uppercase tracking-wider font-medium">{label}</span>
      </div>
    </div>
  );
}

/** Outline button for social/auth-link secondary options. */
export function AuthSecondary({
  children,
  onClick,
  type = 'button',
}: { children: ReactNode; onClick?: () => void; type?: 'button' | 'submit' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-900 px-6 py-3 text-sm font-semibold transition-colors"
    >
      {children}
    </button>
  );
}
